import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import config from '../../config';
import ImageCropperModal from '../common/ImageCropperModal';

const convertSvgToPng = (svgFile, targetSize = 512) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            let svgText = e.target.result;
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(svgText, 'image/svg+xml');
                const svgEl = doc.querySelector('svg');
                if (svgEl) {
                    if (!svgEl.getAttribute('width') || !svgEl.getAttribute('height')) {
                        const viewBox = svgEl.getAttribute('viewBox');
                        if (viewBox) {
                            const parts = viewBox.trim().split(/[\s,]+/);
                            if (parts.length >= 4) {
                                svgEl.setAttribute('width', parts[2]);
                                svgEl.setAttribute('height', parts[3]);
                            }
                        } else {
                            svgEl.setAttribute('width', targetSize.toString());
                            svgEl.setAttribute('height', targetSize.toString());
                        }
                    }
                    const serializer = new XMLSerializer();
                    svgText = serializer.serializeToString(doc);
                }
            } catch (err) {
                console.warn('SVG parse error, using raw text', err);
            }

            const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
            const blobUrl = URL.createObjectURL(blob);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = targetSize;
                canvas.height = targetSize;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, targetSize, targetSize);
                
                const w = img.naturalWidth || img.width || targetSize;
                const h = img.naturalHeight || img.height || targetSize;
                const scale = Math.min(targetSize / w, targetSize / h);
                const drawW = w * scale;
                const drawH = h * scale;
                const dx = (targetSize - drawW) / 2;
                const dy = (targetSize - drawH) / 2;
                
                ctx.drawImage(img, dx, dy, drawW, drawH);
                URL.revokeObjectURL(blobUrl);

                canvas.toBlob((pngBlob) => {
                    if (pngBlob) {
                        const baseName = svgFile.name.replace(/\.svg$/i, '');
                        const pngFile = new File([pngBlob], `${baseName}.png`, { type: 'image/png' });
                        resolve(pngFile);
                    } else {
                        reject(new Error('Canvas toBlob failed'));
                    }
                }, 'image/png');
            };
            img.onerror = (err) => {
                URL.revokeObjectURL(blobUrl);
                reject(err);
            };
            img.src = blobUrl;
        };
        reader.onerror = reject;
        reader.readAsText(svgFile);
    });
};

const AddEditCategoryModal = ({ show, onHide, onSave, category }) => {
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: ''
    });
    const [iconFile, setIconFile] = useState(null);
    const [iconPreview, setIconPreview] = useState(null);

    // Image Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        fixedAspectRatio: 1, // 1:1 for category icon
        initialAspectRatio: 1,
        minWidth: 100 // Smaller min width for icons
    });

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || '',
                slug: category.slug || '',
                description: category.description || ''
            });
            if (category.icon) {
                const base = (config.API_BASE_URL || '').replace(/\/+$/, '');
                const clean = category.icon.replace(/^\/+/, '');
                const iconUrl = category.icon.startsWith('http') 
                    ? category.icon 
                    : `${base}/${clean}`;
                setIconPreview(iconUrl);
            } else {
                setIconPreview(null);
            }
        } else {
            setFormData({ name: '', slug: '', description: '' });
            setIconPreview(null);
        }
        setIconFile(null);
    }, [category]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (10MB limit for source)
            if (file.size > 10 * 1024 * 1024) {
                alert("File size exceeds 10MB limit.");
                e.target.value = '';
                return;
            }
            
            // For SVG icons, convert to a high-res 512x512 transparent PNG
            // so it passes backend image validation and displays perfectly across all devices
            if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
                convertSvgToPng(file, 512)
                    .then(pngFile => {
                        const objectUrl = URL.createObjectURL(pngFile);
                        setIconFile(pngFile);
                        setIconPreview(objectUrl);
                    })
                    .catch(err => {
                        console.error('Failed to convert SVG to PNG:', err);
                        const objectUrl = URL.createObjectURL(file);
                        setIconFile(file);
                        setIconPreview(objectUrl);
                    });
                e.target.value = '';
                return;
            }

            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                fixedAspectRatio: 1, // 1:1 for category icon
                initialAspectRatio: 1,
                minWidth: 100,
                outputFormat: file.type === 'image/png' ? 'image/png' : 'image/jpeg'
            });
            
            // Reset input value
            e.target.value = '';
        }
    };

    const handleCropperHide = () => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        setIconFile(croppedFile);
        setIconPreview(previewUrl);
        handleCropperHide();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Use FormData for file upload
        const data = new FormData();
        data.append('name', formData.name);
        data.append('slug', formData.slug);
        data.append('description', formData.description);
        if (iconFile) {
            data.append('icon', iconFile);
        }

        onSave(data);
    };

    return (
        <Modal show={show} onHide={onHide} centered className="modal-lg">
            <Modal.Header closeButton>
                <Modal.Title className="text-lg fw-semibold">
                    {category ? 'Edit Category' : 'Add New Category'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="row gy-4">
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Category Icon
                            </label>
                            <div className="d-flex align-items-center gap-3">
                                <div className="avatar-upload">
                                    <div className="avatar-preview w-80-px h-80-px rounded-16 overflow-hidden position-relative bg-white border border-neutral-200 d-flex align-items-center justify-content-center p-2 shadow-xs">
                                        {iconPreview ? (
                                            <img src={iconPreview} alt="Icon Preview" className="w-100 h-100 object-fit-contain" />
                                        ) : (
                                            <Icon icon="solar:camera-linear" className="text-secondary-light text-3xl" />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <input 
                                        type="file" 
                                        className="form-control" 
                                        accept="image/png, image/jpeg, image/svg+xml, image/webp"
                                        onChange={handleFileChange}
                                    />
                                    <div className="form-text text-xs mt-1">Allowed: PNG, JPG, SVG, WEBP. Max 2MB.</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Category Name <span className="text-danger-600">*</span>
                            </label>
                            <input 
                                type="text" 
                                className="form-control radius-8" 
                                placeholder="Enter category name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required 
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Slug (Optional)
                            </label>
                            <input 
                                type="text" 
                                className="form-control radius-8" 
                                placeholder="auto-generated-if-empty"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                            />
                            <div className="form-text text-sm">Unique identifier for URLs. Leave empty to auto-generate from name.</div>
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Description
                            </label>
                            <textarea 
                                className="form-control radius-8" 
                                placeholder="Enter category description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="3"
                            ></textarea>
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                        <button type="button" className="hs-btn-secondary" onClick={onHide}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary text-sm btn-sm px-24 py-12 radius-8">
                            {category ? 'Update' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal.Body>
            
            {/* Image Cropper Modal */}
            {cropperState.show && (
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                    outputFormat={cropperState.outputFormat || 'image/png'}
                />
            )}
        </Modal>
    );
};

export default AddEditCategoryModal;
